import React, { useMemo, useState, useEffect, useCallback } from 'react';
import { toast } from 'react-hot-toast';
import { useSupplierCrmOrgStore } from '../../../store/supplierCrmOrg.store';
import { useAuth } from '../../../contexts/AuthContext';
import { projectsService, type Project, type ProjectInput } from '../../../services/projectManagement/projectsService';
import AIProjectAssistant from '../components/AIProjectAssistant';
import PmSavedViewsControl from '../components/PmSavedViewsControl';
import ProjectCustomFieldsModal from '../components/ProjectCustomFieldsModal';
import ProjectTemplatesModal from '../components/ProjectTemplatesModal';
import PmFilterBuilder from '../components/PmFilterBuilder';
import { applyPmFilters, normalizePmFilterState, type PmFilterState } from '../lib/pmFilters';

const ProjectsTab: React.FC = () => {
  const { activeOrgId } = useSupplierCrmOrgStore();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [projects, setProjects] = useState<Project[]>([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [projectFilters, setProjectFilters] = useState<PmFilterState>({ query: '', combinator: 'and', rules: [] });
  const [aiAssistantProject, setAiAssistantProject] = useState<Project | null>(null);
  const [customFieldsProject, setCustomFieldsProject] = useState<Project | null>(null);
  const [showTemplatesModal, setShowTemplatesModal] = useState(false);

  // Form state
  const [formData, setFormData] = useState<Partial<ProjectInput>>({
    name: '',
    description: '',
    status: 'planning',
    priority: 'medium',
    start_date: '',
    due_date: '',
    estimated_hours: 0,
    budget: 0,
    tags: [],
    color: '#3b82f6',
  });

  const fetchProjects = useCallback(async () => {
    if (!activeOrgId) return;
    setLoading(true);
    try {
      const params: any = { organization_id: activeOrgId };
      if (filterStatus !== 'all') params.status = filterStatus;

      const { data, error } = await projectsService.list(params);
      if (error) {
        toast.error('Failed to load projects');
      } else {
        setProjects(data);
      }
    } catch (error) {
      toast.error('Failed to load projects');
    } finally {
      setLoading(false);
    }
  }, [activeOrgId, filterStatus]);

  const projectTagOptions = useMemo(() => {
    const s = new Set<string>();
    projects.forEach(p => (p.tags || []).forEach(t => s.add(String(t))));
    return Array.from(s)
      .filter(Boolean)
      .sort()
      .map(t => ({ value: t, label: t }));
  }, [projects]);

  const projectFilterFields = useMemo(() => {
    return [
      {
        field: 'priority',
        label: 'Priority',
        type: 'select' as const,
        options: [
          { value: 'low', label: 'Low' },
          { value: 'medium', label: 'Medium' },
          { value: 'high', label: 'High' },
          { value: 'urgent', label: 'Urgent' },
        ],
      },
      {
        field: 'due_date',
        label: 'Due Date',
        type: 'date' as const,
      },
      {
        field: 'budget',
        label: 'Budget',
        type: 'number' as const,
      },
      {
        field: 'estimated_hours',
        label: 'Estimated Hours',
        type: 'number' as const,
      },
      {
        field: 'tags',
        label: 'Tags',
        type: 'select' as const,
        options: projectTagOptions,
      },
    ];
  }, [projectTagOptions]);

  const visibleProjects = useMemo(() => {
    return applyPmFilters(projects, projectFilters, ['name', 'description']);
  }, [projects, projectFilters]);

  useEffect(() => {
    void fetchProjects();
  }, [fetchProjects]);

  const handleCreateProject = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!activeOrgId || !user?.id) return;

    try {
      const payload: ProjectInput = {
        organization_id: activeOrgId,
        owner_id: user.id,
        name: formData.name!,
        description: formData.description,
        status: formData.status,
        priority: formData.priority,
        start_date: formData.start_date,
        due_date: formData.due_date,
        estimated_hours: formData.estimated_hours,
        budget: formData.budget,
        tags: formData.tags,
        color: formData.color,
      };

      const { error } = await projectsService.create(payload);
      if (error) {
        toast.error(error);
        return;
      }

      toast.success('Project created successfully');
      setShowCreateModal(false);
      setFormData({
        name: '',
        description: '',
        status: 'planning',
        priority: 'medium',
        start_date: '',
        due_date: '',
        estimated_hours: 0,
        budget: 0,
        tags: [],
        color: '#3b82f6',
      });
      void fetchProjects();
    } catch (error) {
      toast.error('Failed to create project');
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'planning': return 'bg-gray-100 text-gray-800';
      case 'active': return 'bg-green-100 text-green-800';
      case 'on-hold': return 'bg-yellow-100 text-yellow-800';
      case 'completed': return 'bg-blue-100 text-blue-800';
      case 'archived': return 'bg-gray-100 text-gray-500';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'low': return 'text-gray-500';
      case 'medium': return 'text-blue-500';
      case 'high': return 'text-orange-500';
      case 'urgent': return 'text-red-500';
      default: return 'text-gray-500';
    }
  };

  if (loading) {
    return <div className="rounded-xl bg-white p-12 text-center">Loading projects...</div>;
  }

  return (
    <div className="space-y-6">
      {/* Header with Filters */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="rounded-lg border border-gray-300 px-4 py-2 text-sm"
          >
            <option value="all">All Projects</option>
            <option value="planning">Planning</option>
            <option value="active">Active</option>
            <option value="on-hold">On Hold</option>
            <option value="completed">Completed</option>
            <option value="archived">Archived</option>
          </select>

          {activeOrgId && (
            <PmSavedViewsControl
              organizationId={activeOrgId}
              scope="projects"
              getCurrentPayload={() => ({
                filterStatus,
                projectFilters,
              })}
              applyPayload={(payload) => {
                const nextStatus = String((payload as any)?.filterStatus || '');
                const nextProjectFilters = normalizePmFilterState((payload as any)?.projectFilters);

                if (nextStatus) {
                  setFilterStatus(nextStatus);
                }

                setProjectFilters(nextProjectFilters);
              }}
            />
          )}
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowTemplatesModal(true)}
            className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50"
          >
            📚 Templates
          </button>
          <button
            onClick={() => setShowCreateModal(true)}
            className="rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-white hover:bg-primary/90"
          >
            ➕ New Project
          </button>
        </div>
      </div>

      <PmFilterBuilder
        title="Projects"
        placeholder="Search projects..."
        value={projectFilters}
        onChange={setProjectFilters}
        availableFields={projectFilterFields}
      />

      {/* Projects Grid */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {visibleProjects.length === 0 ? (
          <div className="col-span-full rounded-xl border-2 border-dashed border-gray-300 bg-gray-50 p-12 text-center">
            <div className="text-4xl mb-4">📁</div>
            <h3 className="text-lg font-semibold text-gray-900">No Projects Yet</h3>
            <p className="mt-2 text-sm text-gray-600">
              Create your first project to get started
            </p>
            <button
              onClick={() => setShowCreateModal(true)}
              className="mt-4 rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-white hover:bg-primary/90"
            >
              Create Project
            </button>
          </div>
        ) : (
          visibleProjects.map((project) => (
            <div
              key={project.id}
              className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm hover:shadow-md transition-shadow"
            >
              {/* Project Header */}
              <div className="mb-3 flex items-start justify-between">
                <div className="flex items-center gap-2">
                  <div
                    className="h-3 w-3 rounded-full"
                    style={{ backgroundColor: project.color }}
                  />
                  <h3 className="font-semibold text-gray-900">{project.name}</h3>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setAiAssistantProject(project);
                    }}
                    className="rounded-lg bg-purple-50 px-2 py-1 text-xs font-medium text-purple-700 hover:bg-purple-100"
                    title="AI Task Generator"
                  >
                    🤖 AI
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setCustomFieldsProject(project);
                    }}
                    className="rounded-lg bg-gray-50 px-2 py-1 text-xs font-medium text-gray-700 hover:bg-gray-100"
                    title="Custom Fields"
                  >
                    ⚙️ Fields
                  </button>
                  <span className={`rounded-full px-2 py-1 text-xs font-semibold ${getStatusColor(project.status)}`}>
                    {project.status}
                  </span>
                </div>
              </div>

              {/* Description */}
              {project.description && (
                <p className="mb-3 text-sm text-gray-600 line-clamp-2">{project.description}</p>
              )}

              {/* Progress Bar */}
              <div className="mb-3">
                <div className="flex items-center justify-between text-xs text-gray-600 mb-1">
                  <span>Progress</span>
                  <span>{project.completion_percentage}%</span>
                </div>
                <div className="h-2 w-full overflow-hidden rounded-full bg-gray-200">
                  <div
                    className="h-full bg-primary transition-all"
                    style={{ width: `${project.completion_percentage}%` }}
                  />
                </div>
              </div>

              {/* Metadata */}
              <div className="space-y-2 text-xs text-gray-500">
                <div className="flex items-center justify-between">
                  <span className="flex items-center gap-1">
                    <span className={getPriorityColor(project.priority)}>⬤</span>
                    {project.priority}
                  </span>
                  {project.due_date && (
                    <span>
                      Due: {new Date(project.due_date).toLocaleDateString()}
                    </span>
                  )}
                </div>

                {project.budget > 0 && (
                  <div className="flex items-center justify-between">
                    <span>Budget:</span>
                    <span className="font-medium">${project.budget.toLocaleString()}</span>
                  </div>
                )}

                <div className="flex items-center justify-between">
                  <span>Hours:</span>
                  <span>{project.actual_hours} / {project.estimated_hours}h</span>
                </div>
              </div>

              {/* Tags */}
              {project.tags && project.tags.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-1">
                  {project.tags.slice(0, 3).map((tag, index) => (
                    <span
                      key={index}
                      className="rounded-full bg-gray-100 px-2 py-1 text-xs text-gray-600"
                    >
                      {tag}
                    </span>
                  ))}
                  {project.tags.length > 3 && (
                    <span className="rounded-full bg-gray-100 px-2 py-1 text-xs text-gray-600">
                      +{project.tags.length - 3}
                    </span>
                  )}
                </div>
              )}
            </div>
          ))
        )}
      </div>

      {/* Create Project Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-2xl rounded-xl bg-white p-6 shadow-xl max-h-[90vh] overflow-y-auto">
            <h3 className="mb-4 text-lg font-semibold">Create New Project</h3>
            <form onSubmit={handleCreateProject} className="space-y-4">
              <div>
                <label className="mb-2 block text-sm font-medium">Project Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full rounded-lg border px-4 py-2"
                  required
                />
              </div>

              <div>
                <label className="mb-2 block text-sm font-medium">Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={3}
                  className="w-full rounded-lg border px-4 py-2"
                />
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                <div>
                  <label className="mb-2 block text-sm font-medium">Status</label>
                  <select
                    value={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value as any })}
                    className="w-full rounded-lg border px-4 py-2"
                  >
                    <option value="planning">Planning</option>
                    <option value="active">Active</option>
                    <option value="on-hold">On Hold</option>
                    <option value="completed">Completed</option>
                    <option value="archived">Archived</option>
                  </select>
                </div>

                <div>
                  <label className="mb-2 block text-sm font-medium">Priority</label>
                  <select
                    value={formData.priority}
                    onChange={(e) => setFormData({ ...formData, priority: e.target.value as any })}
                    className="w-full rounded-lg border px-4 py-2"
                  >
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                    <option value="urgent">Urgent</option>
                  </select>
                </div>

                <div>
                  <label className="mb-2 block text-sm font-medium">Start Date</label>
                  <input
                    type="date"
                    value={formData.start_date}
                    onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                    className="w-full rounded-lg border px-4 py-2"
                  />
                </div>

                <div>
                  <label className="mb-2 block text-sm font-medium">Due Date</label>
                  <input
                    type="date"
                    value={formData.due_date}
                    onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
                    className="w-full rounded-lg border px-4 py-2"
                  />
                </div>

                <div>
                  <label className="mb-2 block text-sm font-medium">Estimated Hours</label>
                  <input
                    type="number"
                    value={formData.estimated_hours}
                    onChange={(e) => setFormData({ ...formData, estimated_hours: parseInt(e.target.value) })}
                    className="w-full rounded-lg border px-4 py-2"
                  />
                </div>

                <div>
                  <label className="mb-2 block text-sm font-medium">Budget ($)</label>
                  <input
                    type="number"
                    value={formData.budget}
                    onChange={(e) => setFormData({ ...formData, budget: parseInt(e.target.value) })}
                    className="w-full rounded-lg border px-4 py-2"
                  />
                </div>
              </div>

              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="rounded-lg border px-4 py-2 text-sm font-semibold hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-white hover:bg-primary/90"
                >
                  Create Project
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* AI Project Assistant Modal */}
      {aiAssistantProject && activeOrgId && (
        <AIProjectAssistant
          projectId={aiAssistantProject.id}
          projectName={aiAssistantProject.name}
          projectDescription={aiAssistantProject.description}
          organizationId={activeOrgId}
          onClose={() => setAiAssistantProject(null)}
          onTasksGenerated={() => {
            setAiAssistantProject(null);
            void fetchProjects();
          }}
        />
      )}

      {customFieldsProject && activeOrgId && (
        <ProjectCustomFieldsModal
          organizationId={activeOrgId}
          project={customFieldsProject}
          onClose={() => setCustomFieldsProject(null)}
        />
      )}

      {showTemplatesModal && activeOrgId && (
        <ProjectTemplatesModal
          organizationId={activeOrgId}
          projects={projects}
          onClose={() => setShowTemplatesModal(false)}
          onProjectCreated={() => {
            setShowTemplatesModal(false);
            void fetchProjects();
          }}
        />
      )}
    </div>
  );
};

export default ProjectsTab;
